import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Maximize, BedDouble, Bath, ChevronLeft, ChevronRight, Check, Phone, Mail, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PropertyDetailPage() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/properties/${id}`)
      .then((r) => setProperty(r.data))
      .catch(() => toast.error("Bien non trouvé"))
      .finally(() => setLoading(false));
  }, [id]);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const lightboxPrev = useCallback(() => {
    if (!property?.images?.length) return;
    setLightboxIndex((prev) => (prev === 0 ? property.images.length - 1 : prev - 1));
  }, [property]);

  const lightboxNext = useCallback(() => {
    if (!property?.images?.length) return;
    setLightboxIndex((prev) => (prev === property.images.length - 1 ? 0 : prev + 1));
  }, [property]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") lightboxPrev();
      if (e.key === "ArrowRight") lightboxNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightboxOpen, lightboxPrev, lightboxNext]);

  const formatPrice = (price) =>
    new Intl.NumberFormat("fr-CH", {
      style: "currency",
      currency: property?.currency || "CHF",
      maximumFractionDigits: 0,
    }).format(price);

  const handleContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        ...contactForm,
        subject: `Demande pour: ${property.title}`,
        property_id: property.id,
      });
      toast.success("Votre message a été envoyé avec succès");
      setContactForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Erreur lors de l'envoi du message");
    }
    setSending(false);
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-[#777]">Chargement...</div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="pt-20 min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="font-heading text-2xl text-[#333333]">Bien non trouvé</p>
        <Link to="/biens" className="text-[#699ab6] text-sm underline">
          Retour aux biens
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="property-detail-page" className="pt-20">
      {/* Back link */}
      <div className="px-6 lg:px-12 py-6">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/biens"
            data-testid="back-to-properties"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-medium text-[#777] hover:text-[#699ab6] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour aux biens
          </Link>
        </div>
      </div>

      {/* Image Gallery */}
      <div data-testid="property-gallery" className="px-6 lg:px-12 mb-12">
        <div className="max-w-7xl mx-auto">
          <div className="relative aspect-[16/9] lg:aspect-[21/9] overflow-hidden bg-[#F5F5F5] cursor-pointer" onClick={() => openLightbox(activeImage)}>
            <img
              src={property.images?.[activeImage] || "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800"}
              alt={property.title}
              className="w-full h-full object-cover transition-opacity duration-500"
            />
            {property.images?.length > 1 && (
              <>
                <button
                  data-testid="gallery-prev"
                  onClick={(e) => { e.stopPropagation(); setActiveImage((prev) => (prev === 0 ? property.images.length - 1 : prev - 1)); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-[#333]" />
                </button>
                <button
                  data-testid="gallery-next"
                  onClick={(e) => { e.stopPropagation(); setActiveImage((prev) => (prev === property.images.length - 1 ? 0 : prev + 1)); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-[#333]" />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {property.images.map((_, i) => (
                    <button
                      key={i}
                      onClick={(e) => { e.stopPropagation(); setActiveImage(i); }}
                      className={`w-8 h-0.5 transition-all ${
                        i === activeImage ? "bg-white" : "bg-white/40"
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {property.images?.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto">
              {property.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => openLightbox(i)}
                  className={`flex-shrink-0 w-20 h-20 overflow-hidden transition-opacity ${
                    i === activeImage ? "opacity-100 ring-2 ring-[#699ab6]" : "opacity-50 hover:opacity-75"
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && property.images?.length > 0 && (
        <div
          data-testid="photo-lightbox"
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            data-testid="lightbox-close"
            onClick={closeLightbox}
            className="absolute top-6 right-6 z-10 w-10 h-10 bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>

          {/* Counter */}
          <div className="absolute top-6 left-6 text-white/70 text-sm font-medium">
            {lightboxIndex + 1} / {property.images.length}
          </div>

          {/* Image */}
          <img
            src={property.images[lightboxIndex]}
            alt={`${property.title} - Photo ${lightboxIndex + 1}`}
            className="max-w-[90vw] max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Navigation arrows */}
          {property.images.length > 1 && (
            <>
              <button
                data-testid="lightbox-prev"
                onClick={(e) => { e.stopPropagation(); lightboxPrev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
              <button
                data-testid="lightbox-next"
                onClick={(e) => { e.stopPropagation(); lightboxNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            </>
          )}
        </div>
      )}

      {/* Content */}
      <div className="px-6 lg:px-12 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            {/* Main content */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <span className="font-accent italic text-sm text-[#699ab6]">
                  {property.transaction_type === "programme" ? "Programme neuf" : property.transaction_type === "vente" ? "Vente" : property.transaction_type === "location" ? "Location" : property.transaction_type}
                </span>
                <span className="text-[#E5E5E5]">|</span>
                <span className="text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium">
                  {property.property_type}
                </span>
                {property.reference && (
                  <>
                    <span className="text-[#E5E5E5]">|</span>
                    <span className="text-[11px] uppercase tracking-[0.15em] text-[#999] font-medium">
                      Réf. {property.reference}
                    </span>
                  </>
                )}
              </div>

              <h1 data-testid="property-title" className="font-heading text-3xl lg:text-4xl font-medium text-[#333333] tracking-tight mb-4">
                {property.title}
              </h1>

              <div className="flex items-center gap-1.5 mb-8">
                <MapPin className="w-4 h-4 text-[#699ab6]" />
                <span className="text-sm text-[#777]">{property.location}</span>
              </div>

              {/* Specs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 py-8 border-y border-[#E5E5E5] mb-8">
                <div>
                  <p className="font-heading text-2xl font-semibold text-[#699ab6]">
                    {property.price ? formatPrice(property.price) : "Sur demande"}
                  </p>
                  <p className="text-[11px] uppercase tracking-[0.1em] text-[#777] mt-1">Prix</p>
                </div>
                {property.surface && (
                  <div className="flex items-start gap-3">
                    <Maximize className="w-5 h-5 text-[#699ab6] mt-1" />
                    <div>
                      <p className="font-heading text-xl font-semibold text-[#333]">{property.surface} m²</p>
                      <p className="text-[11px] uppercase tracking-[0.1em] text-[#777] mt-1">Surface</p>
                    </div>
                  </div>
                )}
                {property.rooms && (
                  <div className="flex items-start gap-3">
                    <BedDouble className="w-5 h-5 text-[#699ab6] mt-1" />
                    <div>
                      <p className="font-heading text-xl font-semibold text-[#333]">{property.rooms}</p>
                      <p className="text-[11px] uppercase tracking-[0.1em] text-[#777] mt-1">Pièces</p>
                    </div>
                  </div>
                )}
                {(property.bedrooms > 0) && (
                  <div className="flex items-start gap-3">
                    <BedDouble className="w-5 h-5 text-[#699ab6] mt-1" />
                    <div>
                      <p className="font-heading text-xl font-semibold text-[#333]">{property.bedrooms}</p>
                      <p className="text-[11px] uppercase tracking-[0.1em] text-[#777] mt-1">Chambres</p>
                    </div>
                  </div>
                )}
                {property.bathrooms > 0 && (
                  <div className="flex items-start gap-3">
                    <Bath className="w-5 h-5 text-[#699ab6] mt-1" />
                    <div>
                      <p className="font-heading text-xl font-semibold text-[#333]">{property.bathrooms}</p>
                      <p className="text-[11px] uppercase tracking-[0.1em] text-[#777] mt-1">Salles de bain</p>
                    </div>
                  </div>
                )}
              </div>

              <h2 className="font-heading text-xl font-medium text-[#333] mb-4">Description</h2>
              <p data-testid="property-description" className="text-[#777] text-base leading-relaxed font-light mb-8">
                {property.description}
              </p>

              {property.features?.length > 0 && (
                <>
                  <h2 className="font-heading text-xl font-medium text-[#333] mb-4">Caractéristiques</h2>
                  <div className="grid grid-cols-2 gap-3">
                    {property.features.map((feature) => (
                      <div key={feature} className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-[#699ab6]" />
                        <span className="text-sm text-[#777]">{feature}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Contact Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28 bg-[#F5F5F5] p-8">
                <h3 className="font-heading text-xl font-medium text-[#333] mb-2">
                  Intéressé par ce bien ?
                </h3>
                <p className="text-sm text-[#777] font-light mb-6">
                  Contactez-nous pour organiser une visite
                </p>

                <form data-testid="property-contact-form" onSubmit={handleContact} className="space-y-4">
                  <input
                    data-testid="contact-name-input"
                    type="text"
                    placeholder="Votre nom"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    className="input-underline w-full text-sm"
                  />
                  <input
                    data-testid="contact-email-input"
                    type="email"
                    placeholder="Votre email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className="input-underline w-full text-sm"
                  />
                  <input
                    data-testid="contact-phone-input"
                    type="tel"
                    placeholder="Votre téléphone"
                    required
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    className="input-underline w-full text-sm"
                  />
                  <textarea
                    data-testid="contact-message-input"
                    placeholder="Votre message"
                    required
                    rows={4}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    className="input-underline w-full text-sm resize-none"
                  />
                  <button
                    data-testid="contact-submit-btn"
                    type="submit"
                    disabled={sending}
                    className="w-full bg-[#699ab6] text-white py-3.5 text-[11px] uppercase tracking-[0.15em] font-medium hover:bg-[#5a8ba5] transition-colors disabled:opacity-50"
                  >
                    {sending ? "Envoi en cours..." : "Envoyer ma demande"}
                  </button>
                </form>

                <div className="mt-8 pt-6 border-t border-[#E5E5E5] space-y-3">
                  <a
                    href="tel:+41223000262"
                    className="flex items-center gap-3 text-sm text-[#777] hover:text-[#699ab6] transition-colors"
                  >
                    <Phone className="w-4 h-4 text-[#699ab6]" />
                    +41 22 300 02 62
                  </a>
                  <a
                    href="mailto:info@vingt-neuf.ch"
                    className="flex items-center gap-3 text-sm text-[#777] hover:text-[#699ab6] transition-colors"
                  >
                    <Mail className="w-4 h-4 text-[#699ab6]" />
                    info@vingt-neuf.ch
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
